import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { fetchAnnouncements } from './api';
import { AnnouncementEditor } from './AnnouncementEditor';
import { InlineReach } from './ReachStats';
import type { Announcement, AnnouncementAudience, AnnouncementType } from './types';
import { useAppContextStore } from '../app/appContextStore';
import { ALL_APPS, DEFAULT_APP_ID, appLabel } from '../../lib/appConstants';

const TYPE_META: Record<AnnouncementType, { label: string; bg: string; color: string }> = {
  NEWS: { label: 'News', bg: darkTheme.colors.infoBg, color: darkTheme.colors.info },
  UPDATE: { label: 'Update', bg: darkTheme.colors.accentSubtle, color: darkTheme.colors.accentLight },
  PROMO: { label: 'Aktion', bg: darkTheme.colors.warningBg, color: darkTheme.colors.warning },
  TECH: { label: 'Tech', bg: darkTheme.colors.bgElevated, color: darkTheme.colors.textSecondary },
  MAINTENANCE: { label: 'Wartung', bg: darkTheme.colors.dangerBg, color: darkTheme.colors.danger },
};

const AUDIENCE_LABEL: Record<AnnouncementAudience, string> = {
  ALL: 'Alle',
  FREE_ONLY: 'Free',
  PREMIUM_ONLY: 'Pro',
  PREMIUM_PLUS_ONLY: 'Premium+',
};

type StatusFilter = 'all' | 'active' | 'inactive' | 'scheduled' | 'expired';
type TypeFilter = AnnouncementType | 'ALL';

export function AnnouncementsPage(): JSX.Element {
  const [editing, setEditing] = useState<Announcement | null | 'new'>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('ALL');
  const [search, setSearch] = useState('');

  // Ziel-App aus dem globalen Picker; „Alle Apps" → currivium (Announcements
  // sind pro App). Anlegen/Liste/Reach scopen darauf.
  const ctxApp = useAppContextStore((s) => s.appId);
  const app = ctxApp === ALL_APPS ? DEFAULT_APP_ID : ctxApp;

  const query = useQuery({
    queryKey: ['announcements', app],
    queryFn: () => fetchAnnouncements(app),
  });

  const filtered = (query.data ?? []).filter((a) => {
    if (typeFilter !== 'ALL' && a.type !== typeFilter) return false;
    const now = Date.now();
    const status = derivedStatus(a, now);
    if (statusFilter !== 'all' && status !== statusFilter) return false;
    if (search) {
      const t = search.toLowerCase();
      if (!a.title.toLowerCase().includes(t) && !a.body.toLowerCase().includes(t)) return false;
    }
    return true;
  });

  return (
    <div className="mx-auto max-w-[1200px]">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">In-App-Announcements</h1>
          <p className="mt-1 flex items-center gap-2 text-xs text-text-tertiary">
            News, Updates, Aktionen und Tech-Hinweise an die App ausspielen.
            <span
              className="rounded-full px-2 py-0.5 text-[11px]"
              style={{
                background: darkTheme.colors.accentSubtle,
                color: darkTheme.colors.accentLight,
                border: `1px solid ${darkTheme.colors.accent}`,
              }}
              title="Oben im App-Picker wählbar — bestimmt Ziel-App + Anzeige"
            >
              {appLabel(app)}
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="rounded-md px-4 py-2 text-sm font-semibold text-white"
          style={{ background: darkTheme.colors.accent }}
        >
          + Neu anlegen
        </button>
      </header>

      <div
        className="mb-4 flex flex-wrap items-center gap-2 rounded-md border p-3"
        style={{
          background: darkTheme.colors.bgSurface,
          borderColor: darkTheme.colors.borderSubtle,
        }}
      >
        <input
          type="search"
          placeholder="In Titel/Body suchen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[220px] rounded-md border bg-bg-base px-3 py-1.5 text-sm text-text-primary outline-none focus:border-accent"
          style={{ borderColor: darkTheme.colors.borderSubtle }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="rounded-md border bg-bg-base px-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent"
          style={{ borderColor: darkTheme.colors.borderSubtle }}
        >
          <option value="all">Alle Status</option>
          <option value="active">Aktiv</option>
          <option value="scheduled">Geplant</option>
          <option value="expired">Abgelaufen</option>
          <option value="inactive">Inaktiv</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
          className="rounded-md border bg-bg-base px-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent"
          style={{ borderColor: darkTheme.colors.borderSubtle }}
        >
          <option value="ALL">Alle Typen</option>
          {(Object.keys(TYPE_META) as AnnouncementType[]).map((t) => (
            <option key={t} value={t}>
              {TYPE_META[t].label}
            </option>
          ))}
        </select>
      </div>

      {query.isLoading && <SkeletonList />}
      {query.error && (
        <div
          className="rounded-md p-4 text-sm"
          style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
        >
          Fehler: {query.error instanceof Error ? query.error.message : 'unbekannt'}
        </div>
      )}
      {query.data && filtered.length === 0 && (
        <div
          className="rounded-md border p-12 text-center text-sm text-text-secondary"
          style={{
            background: darkTheme.colors.bgSurface,
            borderColor: darkTheme.colors.borderSubtle,
          }}
        >
          Keine Announcements für die aktuellen Filter.
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((a) => (
          <Row key={a.id} announcement={a} app={app} onEdit={() => setEditing(a)} />
        ))}
      </div>

      {editing !== null && (
        <AnnouncementEditor
          existing={editing === 'new' ? null : editing}
          app={app}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function Row({
  announcement,
  app,
  onEdit,
}: {
  announcement: Announcement;
  app: string;
  onEdit: () => void;
}): JSX.Element {
  const meta = TYPE_META[announcement.type];
  const status = derivedStatus(announcement, Date.now());

  return (
    <button
      type="button"
      onClick={onEdit}
      className="block w-full rounded-md border p-4 text-left transition-colors hover:bg-bg-elevated"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
              style={{ background: meta.bg, color: meta.color }}
            >
              {meta.label}
            </span>
            <StatusChip status={status} />
            <span className="text-[11px] text-text-tertiary">
              Audience: {AUDIENCE_LABEL[announcement.audience]} · Priority {announcement.priority}
            </span>
          </div>
          <div className="font-semibold text-text-primary">{announcement.title}</div>
          <div className="mt-1 line-clamp-2 text-sm text-text-secondary">
            {announcement.body}
          </div>
        </div>
        <div className="text-right text-[11px] text-text-tertiary whitespace-nowrap">
          <InlineReach announcementId={announcement.id} app={app} />
          {announcement.scheduledAt && (
            <div className="mt-1">Ab {formatShort(announcement.scheduledAt)}</div>
          )}
          {announcement.expiresAt && <div>Bis {formatShort(announcement.expiresAt)}</div>}
          <div className="mt-1">aktualisiert {formatRelative(announcement.updatedAt)}</div>
        </div>
      </div>
    </button>
  );
}

function StatusChip({ status }: { status: ReturnType<typeof derivedStatus> }): JSX.Element {
  const map: Record<typeof status, { label: string; bg: string; color: string }> = {
    active: { label: 'Aktiv', bg: darkTheme.colors.successBg, color: darkTheme.colors.success },
    scheduled: { label: 'Geplant', bg: darkTheme.colors.infoBg, color: darkTheme.colors.info },
    expired: { label: 'Abgelaufen', bg: darkTheme.colors.bgElevated, color: darkTheme.colors.textTertiary },
    inactive: { label: 'Inaktiv', bg: darkTheme.colors.bgElevated, color: darkTheme.colors.textTertiary },
  };
  const m = map[status];
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      style={{ background: m.bg, color: m.color }}
    >
      {m.label}
    </span>
  );
}

function derivedStatus(
  a: Announcement,
  now: number,
): 'active' | 'inactive' | 'scheduled' | 'expired' {
  if (!a.isActive) return 'inactive';
  if (a.scheduledAt && new Date(a.scheduledAt).getTime() > now) return 'scheduled';
  if (a.expiresAt && new Date(a.expiresAt).getTime() < now) return 'expired';
  return 'active';
}

function SkeletonList(): JSX.Element {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="h-20 animate-pulse rounded-md"
          style={{ background: darkTheme.colors.bgSurface }}
        />
      ))}
    </div>
  );
}

function formatShort(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'gerade eben';
  if (ms < 3_600_000) return `vor ${Math.floor(ms / 60_000)} min`;
  if (ms < 86_400_000) return `vor ${Math.floor(ms / 3_600_000)} h`;
  return new Date(iso).toLocaleDateString('de-DE');
}
